import React, { ReactElement, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useStripe, ElementsConsumer } from "@stripe/react-stripe-js";

import { GET_WINE_NAME_AND_LARGE_PHOTO, GET_TOP_UP_INFO } from "../../../graphql/queries";
import { getCountry } from "../../../helpers/urls.helper";
import { logError } from "../../../helpers/logging";
import {
  DB_ID_ORDER_CATEGORY_ONE_OFF_WINES,
  ORDER_CATEGORY_ID_TO_NAME,
  I18N_NAMESPACES,
  PAYMENT_INTENT_STATUS_SUCCEEDED,
  FACEBOOK_TRACKING_CURRENCY,
} from "../../../helpers/constants";
import { showErrorAlert } from "../../../helpers/alerts";
import urlPatterns from "../../../urls";
import { formatNumber, shouldUseCheckoutV2 } from "../../../helpers/tools";
import { ONE_CLICK_TOP_UP, CREATE_ONE_CLICK_TOP_UP_PAYMENT_INTENT } from "../../../graphql/mutations";

import { LabelTan } from "../../atoms/Label";
import Loading from "../../atoms/Loading";
import ExpressCheckoutWrapper from "../../atoms/ExpressCheckoutButton/ExpressCheckoutWrapper";
import StripeElements from "../../molecules/StripeElements";
import OffCanvasTray from "../OffCanvasTray";
import OffCanvasTrayBody from "../../molecules/OffCanvasTrayBody";
import Button from "../../atoms/Button";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import {
  StyledCheckoutTotalItemPriceBold,
  StyledCheckoutTotalItem,
  StyledCheckoutTotalItemLeft,
  StyledCheckoutTotalItemRight,
} from "../../atoms/CheckoutTotalList";
import AnimatingTrayContainer from "../../atoms/AnimatingTrayContainer";
import { getCashAmountLabel } from "../../../helpers/i18n";
import { GetWinePhotos_wine } from "../../../types/__generated__/GetWinePhotos";
import { GetLatestVintage_latestVintage } from "../../../types/__generated__/GetLatestVintage";
import { TopUpInfo, TopUpInfoVariables } from "../../../types/__generated__/TopUpInfo";
import { OneClickTopup_oneClickTopup } from "../../../types/__generated__/OneClickTopup";
import { CellarBottleHolder } from "../../atoms/CellarBottleHolder";
import RotatingBottleBox from "../../atoms/RotatingBottleBox";
import {
  TopUpTrayHeader,
  StyledSubmitBtnPrice,
  CheckoutTotalItemPriceBoldWithMinWidth,
  StyledOffCanvasTrayFooter,
  TopUpTrayCheckoutTotal,
  StyledAlignCenter,
  StyledAlignCenterSmall,
} from "./styles";

interface TopUpTrayProps {
  topUpWineQuantity: number;
  memberId: number;
  wine: GetWinePhotos_wine | GetLatestVintage_latestVintage;
  baseColor: string;
  bottleImage: string;
  handleOffCanvasTopUpClose: () => void;
  isInBoxAndCartTray?: boolean;
}

// Interface for wallet order context stored between onConfirm and onSuccess
interface WalletOrderContext {
  netTotal: number;
  discountValue?: number | null;
}

const TopUpTrayContent = ({
  topUpWineQuantity,
  memberId,
  wine,
  baseColor,
  bottleImage,
  handleOffCanvasTopUpClose,
  isInBoxAndCartTray = false,
}: TopUpTrayProps): ReactElement => {
  const [isTopupInProgress, setIsTopupInProgress] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);
  const { t } = useTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.STRIPE]);
  const history = useHistory();
  const client = useApolloClient();
  const [oneClickTopup] = useMutation(ONE_CLICK_TOP_UP);
  const [createOneClickTopupPaymentIntent] = useMutation(CREATE_ONE_CLICK_TOP_UP_PAYMENT_INTENT);
  const stripe = useStripe();
  // Store context for wallet payment between onConfirm and onSuccess
  const walletOrderContextRef = useRef<WalletOrderContext | null>(null);

  /**
   * Toggles open/close of the "Add bottle" panel.
   */
  const handleOneClickSubmitChange = () => {
    setIsTopupInProgress((prev) => !prev);
  };

  interface ChargeOneClickTopupResult {
    isSuccessful: boolean;
    errors?: { messages: string[] }[] | null;
    latestOrderId?: number;
  }

  /**
   * Charges the one click topup using the appropriate method.
   */
  const chargeOneClickTopup = async (
    wineId: number | null,
    quantity: number
  ): Promise<ChargeOneClickTopupResult | OneClickTopup_oneClickTopup> => {
    if (shouldUseCheckoutV2()) {
      const {
        data: {
          createOneClickTopupPaymentIntent: { errors, clientSecret, defaultPaymentMethodApiUuid },
        },
      } = await createOneClickTopupPaymentIntent({
        variables: {
          input: {
            wineId,
            quantity,
          },
        },
      });

      if (errors || !clientSecret || !defaultPaymentMethodApiUuid) {
        return { errors, isSuccessful: false };
      }

      const paymentResult = await stripe?.confirmCardPayment(clientSecret, {
        payment_method: defaultPaymentMethodApiUuid,
      });

      if (!paymentResult || paymentResult.error) {
        const errorMessage = t(`stripe:errors.${paymentResult?.error.code}`, {
          defaultValue: paymentResult?.error.message,
        });
        return {
          isSuccessful: false,
          errors: [{ messages: [errorMessage] }],
        };
      }

      return {
        isSuccessful: paymentResult.paymentIntent.status === PAYMENT_INTENT_STATUS_SUCCEEDED,
        errors: null,
      };
    }

    // PLA-250: Remove this legacy method once switch to PaymentIntentAPI is complete
    const {
      data: { oneClickTopup: oneClickTopupData },
    } = await oneClickTopup({
      variables: {
        input: {
          memberId,
          wineId,
          quantity,
        },
      },
    });

    return oneClickTopupData;
  };

  /**
   * Submits a one-off top-up purchase.
   */
  const handleOneClickTopup = async (
    event: React.MouseEvent<HTMLElement>,
    netTotal: number,
    discountValue?: number | null
  ) => {
    event.preventDefault();
    handleOneClickSubmitChange();

    try {
      const { errors, isSuccessful, latestOrderId } = await chargeOneClickTopup(wine.id, topUpWineQuantity);

      if (isSuccessful) {
        history.push(urlPatterns.CHECKOUT_THANK_YOU, {
          checkout: {
            order_id: latestOrderId,
            currency: FACEBOOK_TRACKING_CURRENCY,
            value: formatNumber(netTotal),
            revenue: formatNumber(netTotal),
            content_category: "checkout",
            order_category_id: DB_ID_ORDER_CATEGORY_ONE_OFF_WINES,
            order_category_name: ORDER_CATEGORY_ID_TO_NAME[DB_ID_ORDER_CATEGORY_ONE_OFF_WINES],
            order_discount: discountValue,
          },
          isGift: false,
          isSubscription: false,
        });
      } else if (errors) {
        const errorMessage = errors?.[0]?.messages?.[0] || t("common:sorryTryAgain");
        showErrorAlert(errorMessage);
        handleOneClickSubmitChange();
      } else {
        showErrorAlert(t("common:sorryPaymentDeclined"));
        handleOneClickSubmitChange();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      logError(err, { location: "TopUpTrayV2", action: "CREATE_ONE_CLICK_TOP_UP_PAYMENT_INTENT" });
      showErrorAlert(t("common:sorryTryAgain"));
      handleOneClickSubmitChange();
    }
  };

  /**
   * This function sets the state of shouldHide to true,
   * so, the hiding action can start animating,
   * then, close the tray after 200ms.
   * */
  const handleCloseTray = () => {
    setShouldHide(true);
    setTimeout(() => handleOffCanvasTopUpClose(), 200);
  };

  // Get country-specific currency info for wallet pay
  const country = getCountry();
  const currency = country === "au" ? "aud" : "gbp";

  /**
   * Handle wallet confirm - creates PaymentIntent and returns clientSecret
   */
  const handleWalletConfirm = async (netTotal: number, discountValue?: number | null): Promise<string> => {
    handleOneClickSubmitChange();

    // Store context for onSuccess callback
    walletOrderContextRef.current = { netTotal, discountValue };

    try {
      const {
        data: {
          createOneClickTopupPaymentIntent: { errors, clientSecret },
        },
      } = await createOneClickTopupPaymentIntent({
        variables: {
          input: {
            wineId: wine.id,
            quantity: topUpWineQuantity,
          },
        },
      });

      if (errors || !clientSecret) {
        const errorMessage = errors?.[0]?.messages?.[0] || t("common:sorryTryAgain");
        showErrorAlert(errorMessage);
        handleOneClickSubmitChange();
        throw new Error(errorMessage);
      }

      return clientSecret;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      logError(err, { location: "TopUpTrayV2", action: "WALLET_CONFIRM" });
      handleOneClickSubmitChange();
      throw err;
    }
  };

  /**
   * Handle wallet success - redirects to thank you page
   */
  const handleWalletSuccess = () => {
    const context = walletOrderContextRef.current;
    if (!context) {
      logError(new Error("Wallet order context not found"), { location: "TopUpTrayV2" });
      handleOneClickSubmitChange();
      return;
    }

    const { netTotal, discountValue } = context;

    history.push(urlPatterns.CHECKOUT_THANK_YOU, {
      checkout: {
        currency: FACEBOOK_TRACKING_CURRENCY,
        value: formatNumber(netTotal),
        revenue: formatNumber(netTotal),
        content_category: "checkout",
        order_category_id: DB_ID_ORDER_CATEGORY_ONE_OFF_WINES,
        order_category_name: ORDER_CATEGORY_ID_TO_NAME[DB_ID_ORDER_CATEGORY_ONE_OFF_WINES],
        order_discount: discountValue,
      },
      isGift: false,
      isSubscription: false,
    });
  };

  /**
   * Handle wallet error
   */
  const handleWalletError = (error: Error) => {
    logError(error, { location: "TopUpTrayV2", action: "WALLET_PAYMENT" });
    showErrorAlert(error.message || t("common:sorryPaymentDeclined"));
    handleOneClickSubmitChange();
  };

  const { loading, error, data } = useQuery<TopUpInfo, TopUpInfoVariables>(GET_TOP_UP_INFO, {
    variables: {
      memberId,
      wineId: wine.id!,
      quantity: topUpWineQuantity,
    },
    skip: !wine.id,
    partialRefetch: true,
  });
  if (error) logError(error, { location: "TopUpTrayV2", action: "GET_TOP_UP_INFO" });
  const { topupSummary } = data || {};
  const { totalPrice, discountValue, shippingFee, hasStock } = topupSummary || {};
  const netTotal = (totalPrice ?? 0) - (discountValue ?? 0) + (shippingFee ?? 0);

  if (data && !hasStock) {
    // If this point is reached, it indicates that the stock has been depleted since the last check.
    // To handle this scenario, we initiate a refetch of the GET_WINE_NAME_AND_LARGE_PHOTO query.
    // If all stock has been depleted, the SoldOutOptions will be displayed.
    // Otherwise, this component will present a message along with a back button to go back and add singles.
    client.refetchQueries({
      include: [GET_WINE_NAME_AND_LARGE_PHOTO],
    });
  }

  return (
    <OffCanvasTray
      setOverflowHidden={false}
      isOffcanvasOpened
      outSideClickHandler={() => handleCloseTray()}
      isInBoxAndCartTray={isInBoxAndCartTray}
    >
      <AnimatingTrayContainer isOpened hide={shouldHide}>
        <TopUpTrayHeader
          // @ts-ignore - withRouter strips prop types
          onCloseButtonClick={() => handleCloseTray()}
          title={t("common:bottlesTopup", { count: topUpWineQuantity })}
          center
          isBackButtonCross={false}
        />
        {loading ? (
          <Loading />
        ) : (
          <>
            <CellarBottleHolder baseColor={baseColor}>
              <RotatingBottleBox imgUrl={bottleImage} rotateMobile />
            </CellarBottleHolder>
            <OffCanvasTrayBody>
              <StyledAlignCenter>
                <p>
                  {hasStock
                    ? t("common:topupOrdersDispatchedImmediately")
                    : t("common:sorryNotEnoughStockTopup")}
                </p>
              </StyledAlignCenter>
            </OffCanvasTrayBody>

            <StyledOffCanvasTrayFooter>
              {hasStock ? (
                <>
                  <TopUpTrayCheckoutTotal>
                    <StyledCheckoutTotalItem>
                      <StyledCheckoutTotalItemLeft>{t("common:wineTotal")}</StyledCheckoutTotalItemLeft>

                      <StyledCheckoutTotalItemRight>
                        <StyledCheckoutTotalItemPriceBold>
                          {getCashAmountLabel(totalPrice)}
                        </StyledCheckoutTotalItemPriceBold>
                      </StyledCheckoutTotalItemRight>
                    </StyledCheckoutTotalItem>

                    <StyledCheckoutTotalItem>
                      <StyledCheckoutTotalItemLeft>{t("common:discount")}</StyledCheckoutTotalItemLeft>

                      <StyledCheckoutTotalItemRight>
                        <LabelTan>{t("common:10PercentTopupSaving")}</LabelTan>
                        <CheckoutTotalItemPriceBoldWithMinWidth>
                          {getCashAmountLabel(discountValue && -discountValue)}
                        </CheckoutTotalItemPriceBoldWithMinWidth>
                      </StyledCheckoutTotalItemRight>
                    </StyledCheckoutTotalItem>

                    <StyledCheckoutTotalItem>
                      <StyledCheckoutTotalItemLeft>{t("common:shipping")}</StyledCheckoutTotalItemLeft>

                      <StyledCheckoutTotalItemRight>
                        <StyledCheckoutTotalItemPriceBold>
                          {shippingFee === 0 ? t("common:free") : getCashAmountLabel(shippingFee)}
                        </StyledCheckoutTotalItemPriceBold>
                      </StyledCheckoutTotalItemRight>
                    </StyledCheckoutTotalItem>
                  </TopUpTrayCheckoutTotal>
                  <ExpressCheckoutWrapper
                    amount={Math.round(netTotal * 100)}
                    currency={currency}
                    onConfirm={() => handleWalletConfirm(netTotal, discountValue)}
                    onSuccess={handleWalletSuccess}
                    onError={handleWalletError}
                    disabled={isTopupInProgress}
                  />
                  <Button
                    onClick={(event) => handleOneClickTopup(event, netTotal, discountValue)}
                    disabled={isTopupInProgress}
                    between
                    full
                  >
                    <span>{isTopupInProgress ? t("common:processing") : t("common:confirmPurchase")}</span>
                    <StyledSubmitBtnPrice>{getCashAmountLabel(netTotal)}</StyledSubmitBtnPrice>
                  </Button>
                  <StyledAlignCenterSmall>{t("common:paymentProcessedImmediately")}</StyledAlignCenterSmall>
                </>
              ) : (
                <Button onClick={handleCloseTray} full lg>
                  {t("common:goBack")}
                </Button>
              )}
            </StyledOffCanvasTrayFooter>
          </>
        )}
        ;
      </AnimatingTrayContainer>
    </OffCanvasTray>
  );
};

const TopUpTray = (props: TopUpTrayProps) => (
  <StripeElements>
    <ElementsConsumer>
      {({ stripe }) => {
        if (!stripe) return <StyledLoading />;

        return <TopUpTrayContent {...props} />;
      }}
    </ElementsConsumer>
  </StripeElements>
);

export default TopUpTray;
